import {KAError} from "./ka-error.ts";
import {
    Errors,
    type AmbiguousError,
    type ErrorKind,
    type LogErrorOptions,
} from "./types.ts";

const NEW_KAERROR_STACK_FRAMES = 3;

/**
 * Takes an ambiguous error and returns a KAError representing that error.
 *
 * The returned simplified error pretends to be a real Error object. This way
 * Sentry will also log it as such and we get full stacktrace integration.
 */
export const toKAError = (
    error: AmbiguousError,
    kind: ErrorKind,
    options?: LogErrorOptions | null,
): KAError => {
    if (error == null) {
        // No one should be calling with null/undefined.
        throw new KAError("Cannot simplify undefined or null", Errors.Internal);
    }

    // If we have a string, let's turn that into a KAError.
    if (typeof error === "string") {
        /**
         * Because KAError is an Error, it will make a stack for itself.
         *
         * However, this was created within our logging framework, rather
         * than a throw, so to give better messaging, we need to strip some
         * calls from the stack.
         *
         * This is tricky as after minification, we can't easily identify
         * those calls. However, we know it is three calls:
         *
         *    LazyLog.error -> Log.error -> toKAError
         *
         * We can assert that in a test and rely on minification retaining
         * three calls, even if they no longer have recognizable names.
         *
         * All that said, we can't detect when this should happen or how many
         * frames should be stripped. To do that reliably, we must just specify
         * how many stack frames to drop at the callsite when we know, like
         * right here.
         */
        return new KAError(error, kind, {
            ...options,
            stripStackFrames: NEW_KAERROR_STACK_FRAMES,
        });
    }

    /**
     * OK, is it a KAError?
     */
    if (error instanceof KAError) {
        /**
         * It's a KAError. We don't override the kind here since it
         * already has one. We'll let the calling code work that out.
         * Same with the sourceError, it's meaningless in this circumstance.
         */
        return error as any;
    }

    /**
     * Perhaps we have a regular error or something pretending to be one.
     *
     * We can most broadly check for an error like this.
     */
    const errorString = Object.prototype.toString.call(error);
    if (errorString === "[object Error]" || typeof error.message === "string") {
        /**
         * It's something other than a KAError.
         *
         * So, we're going to turn it into one.
         *
         * Note that here, the cause option is unexpected and ignored.
         *
         * As with string calls, we are possibly generating our own stack here
         * and we want to still use the callsite, so we pass the count of frames
         * to strip.
         */
        return new KAError(error.message, kind, {
            ...options,
            cause: error,
            stripStackFrames: NEW_KAERROR_STACK_FRAMES,
        });
    }

    /**
     * If we get here, we have no idea what's going on. Let's report an error
     * as it's likely our conversion code needs fixing.
     *
     * We don't want to strip stack frames here as we getting here means we
     * might need to update this function to cope better with whatever error
     * object it received. We also use the unstripped version of the stack for
     * verifying that the NEW_KAERROR_STACK_FRAMES value is correct.
     */
    throw new KAError("Cannot standardize error", Errors.Internal, {
        ...options,
        metadata: {
            // We know this isn't an error at this point and is therefore
            // likely to be a primitive value.
            error: error as any,
        },
    });
};
